import * as T from 'frontend/@types';
import { Dropbox } from 'dropbox';
import { createSelector } from 'reselect';
import {
  ensureExists,
  getDirName,
  getUrlForFile,
  UnhandledCaseError,
  getPDFJS,
} from 'frontend/utils';
import {
  parseChordPro,
  SongKey,
  transposeSongKeyByHalfSteps,
  transposeParsedSong,
} from 'frontend/logic/parse-chords';
import { parseMarkdown, splitFrontmatter } from 'frontend/logic/parse-anki';
import { parseSearchString } from 'frontend/logic/search';
import { DropboxFS } from 'frontend/logic/file-store/dropbox-fs';
import {
  DROPBOX_CACHE_KEY,
  FileStore,
  getServerCacheKey,
  isCacheEnabled,
} from 'frontend/logic/file-store';
import { ServerFS } from 'frontend/logic/file-store/server-fs';
import * as AppLogic from 'frontend/logic/app-logic';

type State = T.State;

export * from './language-coach';
export * from './music';

export function getView(state: State) {
  return state.view;
}

export function getDropboxOauth(state: State) {
  return state.dropboxOauth;
}

export function getServers(state: State) {
  return state.servers;
}

export function getServerId(state: State) {
  return state.serverId;
}

export function getListFilesCache(state: State) {
  return state.listFilesCache;
}

export function getDownloadFileCache(state: State) {
  return state.downloadFileCache;
}

export function getDownloadFileErrors(state: State) {
  return state.downloadFileErrors;
}

export function getListFilesErrors(state: State) {
  return state.listFileErrors;
}

export function getDownloadBlobCache(state: State) {
  return state.downloadBlobCache;
}

export function getPath(state: State) {
  return state.path;
}

export function getModifiedTextByPath(state: State) {
  return state.modifiedTextByPath;
}

export function getMessages(state: State) {
  return state.messages;
}

export function getHasOnboarded(state: State) {
  return state.hasOnboarded;
}

export function getExperimentalFeatures(state: State) {
  return state.experimentalFeatures;
}

export function getFileStoreCacheSettings(state: State) {
  return state.fileStoreCacheEnabled;
}

export function getFileStoreCacheBudget(state: State) {
  return state.fileStoreCacheBudget;
}

export function getInputMode(state: State) {
  return state.inputMode;
}

export function getDetectedPointerType(state: State) {
  return state.detectedPointerType;
}

export function getResolvedInputMode(state: State): T.ResolvedInputMode {
  if (state.inputMode === 'auto') {
    return state.detectedPointerType;
  }
  return state.inputMode;
}

export function getEditorAutocompleteSettings(state: State) {
  return state.editorAutocompleteSettings;
}

export function getMusicImportQuality(state: State) {
  return state.musicImportQuality;
}

export function getHideEditor(state: State) {
  return state.hideEditor;
}

export function getEditorOnly(state: State) {
  return state.editorOnly;
}

export function getIsDraggingSplitter(state: State) {
  return state.isDraggingSplitter;
}

export function shouldHideHeader(state: State) {
  return state.shouldHideHeader;
}

export function getCurrentFileStoreName(state: State) {
  return state.currentFileStoreName;
}

export function getFileFocusByPath(state: State) {
  return state.fileFocusByPath;
}

export function getFileSelectionByPath(state: State) {
  return state.fileSelectionByPath;
}

/**
 * The file store slug is either the server id or the current file store name, like
 * "dropbox".
 */
export function getCurrentFileStoreSlug(state: State) {
  return state.serverId ?? state.currentFileStoreName;
}

export function getRenameFile(state: State) {
  return state.renameFile;
}

export function getFilesIndex(state: State) {
  return state.filesIndex;
}

export function getSearchString(state: State) {
  return state.searchString;
}

export function getIDBFSOrNull(state: State) {
  return state.idbfs;
}

export function getWorkerClient(state: State) {
  return state.workerClient;
}

export function getOpenAIApiKey(state: State) {
  return state.openAIApiKey;
}

export function getCopyFile(state: State) {
  return state.copyFile;
}

export function getDraggedFiles(state: State) {
  return state.draggedFiles;
}

export function getFileStoreDisplayName(state: State): string {
  return AppLogic.getFileStoreDisplayName(
    getCurrentFileStoreName(state),
    getCurrentServerOrNull(state),
  );
}

/**
 * Returns the value of the selector and assert that it is non-null.
 */
export function dangerousSelector<T>(
  selector: (state: State) => T | null,
  message: string,
): (state: State) => T {
  return (state) => ensureExists(selector(state), message);
}

export const getDropboxOrNull = createSelector(
  getDropboxOauth,
  (oauth): Dropbox | null => {
    if (!oauth) {
      return null;
    }
    const { accessToken } = oauth;
    // Initiate dropbox.
    const dropbox = new Dropbox({ accessToken });
    // Intercept all calls to dropbox and log them.
    const fakeDropbox: Record<string, any> = {};

    for (const key in dropbox) {
      fakeDropbox[key] = (...args: any[]) => {
        // First log the request.
        const style = 'color: #006DFF; font-weight: bold';
        if (process.env.NODE_ENV !== 'test') {
          console.log(`[dropbox] calling %c"${key}"`, style, ...args);
        }

        // Monitor the response, and pass on the promise result.
        return new Promise((resolve, reject) => {
          const result = (dropbox as any)[key](...args);
          result.then(
            (response: any) => {
              if (process.env.NODE_ENV !== 'test') {
                console.log(`[dropbox] response %c"${key}"`, style, response);
              }
              resolve(response);
            },
            (error: any) => {
              if (process.env.NODE_ENV !== 'test') {
                console.log(`[dropbox] error %c"${key}"`, style, args, error);
              }
              reject(error);
            },
          );
        });
      };
    }
    return fakeDropbox as any;
  },
);

export const getDropboxCacheEnabled = createSelector(
  getFileStoreCacheSettings,
  (settings) => isCacheEnabled(settings, DROPBOX_CACHE_KEY),
);

export const getDropboxFSOrNull = createSelector(
  getDropboxOrNull,
  getDropboxCacheEnabled,
  getWorkerClient,
  (dropbox, cacheEnabled, workerClient) =>
    dropbox ? new DropboxFS(dropbox, cacheEnabled, workerClient) : null,
);

export const getIsDropboxInitiallyExpired = createSelector(
  getDropboxOauth,
  (oauth) => {
    if (!oauth) {
      return false;
    }
    return oauth.expires < Date.now();
  },
);

/**
 * Use this selector on the top-most components when the server isn't known to exist.
 * Then in sub-components use the dangerous version below, which can throw.
 */
export const getCurrentServerOrNull = createSelector(
  getServers,
  getServerId,
  (servers, serverId) => {
    if (!serverId) {
      return null;
    }
    return servers.find((server) => server.id === serverId) ?? null;
  },
);

/**
 * Use this selector inside of sub-components when the server is known to exist.
 */
export const getCurrentServer = dangerousSelector(
  getCurrentServerOrNull,
  'The current server is not available.',
);

export const getCurrentServerCacheEnabled = createSelector(
  getCurrentServerOrNull,
  getFileStoreCacheSettings,
  (server, settings) =>
    server ? isCacheEnabled(settings, getServerCacheKey(server.id)) : false,
);

export const getServerFSOrNull = createSelector(
  getCurrentServerOrNull,
  getCurrentServerCacheEnabled,
  getWorkerClient,
  (server, cacheEnabled, workerClient) => {
    if (!server) {
      return null;
    }
    return new ServerFS(server, cacheEnabled, workerClient);
  },
);

export const getCurrentFSOrNull = createSelector(
  getCurrentFileStoreName,
  getDropboxFSOrNull,
  getIDBFSOrNull,
  getServerFSOrNull,
  getWorkerClient,
  (fsName, dropbox, idbfs, server, workerClient): FileStore | null => {
    switch (fsName) {
      case 'dropbox': {
        return dropbox;
      }
      case 'browser': {
        if (idbfs) {
          idbfs.workerClient = workerClient;
        }
        return idbfs;
      }
      case 'server': {
        return server;
      }
      default:
        throw new UnhandledCaseError(fsName, 'FileStoreName');
    }
  },
);

export const getCurrentFS = dangerousSelector(
  getCurrentFSOrNull,
  'The current file store is not available.',
);

export const getActiveFileOrNull = createSelector(
  getDownloadFileCache,
  getPath,
  (downloadFileCache, path): T.DownloadedTextFile | null => {
    return downloadFileCache.get(path) ?? null;
  },
);

export const getModifiedText = createSelector(
  getModifiedTextByPath,
  getPath,
  (modifiedTextByPath, path) =>
    modifiedTextByPath.get(path) ?? { path, generation: 0, text: null },
);

export const getActiveFileTextOrNull = createSelector(
  getActiveFileOrNull,
  getModifiedText,
  (activeFile, modifiedText): string | null => {
    if (!activeFile) {
      return null;
    }
    if (modifiedText.text !== null) {
      return modifiedText.text;
    }

    return activeFile.text;
  },
);

export const getIsActiveFileModified = createSelector(
  getActiveFileOrNull,
  getModifiedText,
  (activeFile, modifiedText): boolean => {
    if (!activeFile || modifiedText.text === null) {
      return false;
    }
    return modifiedText.text !== activeFile.text;
  },
);

export const getActiveFileText = dangerousSelector(
  getActiveFileTextOrNull,
  'Active file was not downloaded while getting text.',
);

export const getActiveFileParsedOrNull = createSelector(
  getActiveFileTextOrNull,
  (text) => {
    if (text === null) {
      return null;
    }
    return parseChordPro(text);
  },
);

const getActiveFileMarkdownBodyOrNull = createSelector(
  getActiveFileTextOrNull,
  (text): string | null => (text === null ? null : splitFrontmatter(text).body),
);

export const getActiveFileMarkdownOrNull = createSelector(
  getActiveFileMarkdownBodyOrNull,
  (body): T.ParsedMarkdown | null => {
    if (body === null) {
      return null;
    }
    return parseMarkdown(body);
  },
);

export const getActiveFileMarkdown = dangerousSelector(
  getActiveFileMarkdownOrNull,
  'Active file was not downloaded for markdown.',
);

export const getActiveBlobOrNull = createSelector(
  getDownloadBlobCache,
  getPath,
  (downloadBlobCache, path): Blob | null =>
    downloadBlobCache.get(path)?.blob ?? null,
);

export const getActiveBlob = dangerousSelector(
  getActiveBlobOrNull,
  'Active file was not downloaded while getting text.',
);

export const getActivePDFOrNull = createSelector(
  getActiveBlobOrNull,
  async (blob) => {
    if (!blob) {
      return null;
    }
    const pdfjs = await getPDFJS();
    return pdfjs.getDocument(await blob.arrayBuffer()).promise;
  },
);

export const getActivePDF = dangerousSelector(
  getActivePDFOrNull,
  'Active file was not downloaded while parsing file.',
);

export const getActiveImageOrNull = createSelector(
  getActiveBlobOrNull,
  (blob) => {
    if (!blob) {
      return null;
    }
    return URL.createObjectURL(blob);
  },
);

export const getActiveImage = dangerousSelector(
  getActiveImageOrNull,
  'Active file was not downloaded while processing file.',
);

export const getActiveFileSongKeyRaw = createSelector(
  getActiveFileParsedOrNull,
  (activeFile): string | null => {
    if (typeof activeFile?.directives.key === 'string') {
      return activeFile?.directives.key;
    }
    return null;
  },
);

export const getActiveFileTransposeRaw = createSelector(
  getActiveFileParsedOrNull,
  (activeFile): string | null => {
    if (typeof activeFile?.directives.transpose === 'string') {
      return activeFile?.directives.transpose;
    }
    return null;
  },
);

export const getActiveFileCapoRaw = createSelector(
  getActiveFileParsedOrNull,
  (activeFile): string | null => {
    if (typeof activeFile?.directives.capo === 'string') {
      return activeFile?.directives.capo;
    }
    return null;
  },
);

export const getActiveFileCapoValue = createSelector(
  getActiveFileCapoRaw,
  (capoRaw): number | null => {
    if (!capoRaw) {
      return null;
    }
    const value = Number.parseInt(capoRaw, 10);
    if (!Number.isFinite(value) || value < 0) {
      return null;
    }
    return value;
  },
);

export const getActiveFileSongTitleOrNull = createSelector(
  getActiveFileParsedOrNull,
  (parsedFile): string | null => {
    if (!parsedFile) {
      return null;
    }
    const { directives } = parsedFile;
    if (typeof directives.title === 'string') {
      return directives.title;
    }
    return null;
  },
);

export const getActiveFileSongTitle = dangerousSelector(
  getActiveFileSongTitleOrNull,
  'Active file was not downloaded when getting song title.',
);

export const getActiveFileDisplayPath = createSelector(
  getPath,
  getDownloadFileCache,
  getListFilesCache,
  (path, downloadFilesCache, listFilesCache) => {
    const downloadedTextFile = downloadFilesCache.get(path);

    if (downloadedTextFile) {
      return downloadedTextFile.metadata.path;
    }

    const files = listFilesCache.get(path)?.files;
    if (files) {
      const file = files.find((file) => file.path === path);
      if (file?.path) {
        return file.path;
      }
      const activeFileWithSlash = path + '/';
      for (const file of files) {
        if (file?.path?.startsWith(activeFileWithSlash) && file.path) {
          const parts = path.split('/');
          const displayParts = file.path.split('/');
          return displayParts.slice(0, parts.length).join('/');
        }
      }
    }

    return path;
  },
);

export const getActiveFileSongKey = createSelector(
  getActiveFileParsedOrNull,
  getActiveFileSongKeyRaw,
  getActiveFileTransposeRaw,
  getActiveFileCapoValue,
  (parsed, text, transposeRaw, capoValue): SongKey | null => {
    if (
      parsed?.directives.chords === 'nashville' ||
      parsed?.directives.chords === 'roman'
    ) {
      return SongKey.fromRaw(text);
    }
    const baseKey = transposeRaw
      ? SongKey.fromRaw(transposeRaw)
      : SongKey.fromRaw(text);
    if (!baseKey) {
      return null;
    }
    if (capoValue !== null) {
      return transposeSongKeyByHalfSteps(baseKey, -capoValue);
    }
    return baseKey;
  },
);

export const getActiveFileParsedTransformedOrNull = createSelector(
  getActiveFileParsedOrNull,
  getActiveFileSongKeyRaw,
  getActiveFileTransposeRaw,
  getActiveFileCapoValue,
  (parsed, songKeyRaw, transposeRaw, capoValue) => {
    if (!parsed) {
      return null;
    }
    if (
      parsed.directives.chords === 'nashville' ||
      parsed.directives.chords === 'roman'
    ) {
      return parsed;
    }
    if (!songKeyRaw) {
      return parsed;
    }
    const originalKey = SongKey.fromRaw(songKeyRaw);
    if (!originalKey) {
      return parsed;
    }
    const baseKey = transposeRaw
      ? SongKey.fromRaw(transposeRaw)
      : SongKey.fromRaw(songKeyRaw);
    const targetKey =
      baseKey && capoValue !== null
        ? transposeSongKeyByHalfSteps(baseKey, -capoValue)
        : baseKey;
    if (!targetKey) {
      return parsed;
    }
    if (originalKey.display === targetKey.display) {
      return parsed;
    }
    return transposeParsedSong(parsed, targetKey);
  },
);

export const getActiveFileParsedTransformed = dangerousSelector(
  getActiveFileParsedTransformedOrNull,
  'Active file was not downloaded while parsing file.',
);

export function canGoFullScreen(state: State) {
  const view = getView(state);
  switch (view) {
    case null:
      return false;
    case 'view-file':
    case 'view-markdown':
      return document.fullscreenEnabled && getHideEditor(state);
    case 'view-pdf':
    case 'view-image':
      return document.fullscreenEnabled;
    case 'list-files':
    case 'music':
    case 'settings':
    case 'connect':
    case 'connect-folder':
    case 'language-coach':
      return false;
    default:
      throw new UnhandledCaseError(view, 'view');
  }
}

interface SongLink {
  url: string;
  name: string;
}

type NextPrevSong = Partial<{
  nextSong: SongLink;
  prevSong: SongLink;
}>;

export const getNextPrevSong = createSelector(
  getPath,
  getCurrentFileStoreSlug,
  getListFilesCache,
  (path, fsName, listFilesCache): NextPrevSong => {
    const results: NextPrevSong = {};
    const folder = getDirName(path);

    // Only return values if the folders are requested.
    const listFiles = listFilesCache.get(folder)?.files;
    if (!listFiles) {
      return results;
    }

    const pathLower = path.toLowerCase();

    const songLinks: SongLink[] = [];
    let index: number | null = null;
    for (const file of listFiles) {
      // Ignore folders.
      if (file.type === 'folder') {
        continue;
      }

      // Ensure it's something we can open.
      const url = getUrlForFile(fsName, file.path ?? '');
      if (!url) {
        continue;
      }

      // See if this is the current URL.
      if (file.path.toLowerCase() === pathLower) {
        index = songLinks.length;
      }

      songLinks.push({
        url,
        name: file.name,
      });
    }

    // Now determine the previous and next songs from the available songs.

    if (index === null) {
      return results;
    }

    if (index > 0) {
      results.prevSong = songLinks[index - 1];
    }

    if (index < songLinks.length - 1) {
      results.nextSong = songLinks[index + 1];
    }

    return results;
  },
);

export const getParsedSearch = createSelector(
  getSearchString,
  parseSearchString,
);

export const getSearchFilteredFiles = createSelector(
  getParsedSearch,
  getListFilesCache,
  getPath,
  getFilesIndex,
  (
    parsedSearch,
    listFilesCache,
    path,
    filesIndex,
  ): (T.FileMetadata | T.FolderMetadata)[] | null => {
    const listFiles = listFilesCache.get(path)?.files.filter(
      // Remove any dot files.
      (entry) => entry.name[0] !== '.',
    );

    if (!listFiles) {
      return null;
    }

    if (!parsedSearch) {
      return AppLogic.sortFiles(listFiles);
    }

    const { query, inFolder, path: searchPath, directives } = parsedSearch;
    // Do a full filesIndex search.
    let searchedFiles: Array<T.FileMetadata | T.FolderMetadata> = [];
    if (filesIndex) {
      let results = filesIndex.data.files;

      if (searchPath) {
        results = results.filter((fileIndex) =>
          fileIndex.metadata.path.toLowerCase().includes(searchPath),
        );
      }

      if (inFolder) {
        results = results.filter((fileIndex) =>
          fileIndex.metadata.path.toLowerCase().startsWith(inFolder),
        );
      }

      if (query.length > 0) {
        results = results.filter((fileIndex) => {
          for (const queryTerm of query) {
            // Match the path
            if (fileIndex.metadata.path.toLowerCase().includes(queryTerm)) {
              continue;
            }

            // Search through all of the directives
            for (const value of Object.values(fileIndex.directives)) {
              if (value.toLowerCase().includes(queryTerm)) {
                continue;
              }
            }

            // No match was found.
            return false;
          }

          // All query terms matched.
          return true;
        });
      }

      if (directives && Object.values(directives).length > 0) {
        results = results.filter((fileIndex) => {
          for (const [keyTerm, valueTerm] of Object.entries(directives)) {
            const fileValue = fileIndex.directives[keyTerm]?.toLowerCase();
            if (valueTerm === fileValue || (!valueTerm && !fileValue)) {
              continue;
            }
            return false;
          }

          // All directive terms match.
          return true;
        });
      }
      searchedFiles = results.map((fileIndex) => fileIndex.metadata);
    } else {
      if (query.length > 0) {
        searchedFiles = listFiles.filter((metadata) => {
          for (const queryTerm of query) {
            // Match the path
            if (metadata.path.toLowerCase().includes(queryTerm)) {
              continue;
            }

            // No match was found.
            return false;
          }

          // All query terms matched.
          return true;
        });
      }
    }

    return AppLogic.sortFiles(searchedFiles);
  },
);

/**
 * Get the current file focus for the path. This is the name of the file.
 */
export const getFileFocus = createSelector(
  getFileFocusByPath,
  getPath,
  (fileFocusByPath, path): string | null => {
    return fileFocusByPath[path] ?? null;
  },
);

/**
 * Gets the current file focus index for the the current path. This is the index
 * into the search filtered files.
 */
export const getFileFocusIndex = createSelector(
  getFileFocus,
  getSearchFilteredFiles,
  (fileFocus, files) => {
    const index = files?.findIndex((file) => file.name === fileFocus);
    if (!index || index === -1) {
      return 0;
    }
    return index;
  },
);

/**
 * Get the multi-select set of file names for the current path. This is
 * additive to getFileFocus, which tracks the single keyboard/menu cursor.
 */
export const getFileSelection = createSelector(
  getFileSelectionByPath,
  getPath,
  (fileSelectionByPath, path): string[] => {
    return fileSelectionByPath[path] ?? [];
  },
);

export const getFileSelectionSet = createSelector(
  getFileSelection,
  (fileSelection): Set<string> => new Set(fileSelection),
);
